import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    api_base_url = environment.apiUrl

    isAuthenticated(): boolean {
        // Check if there's a valid token in local storage or session storage
        const token = localStorage.getItem('token');
        // Additional checks if needed (e.g., token expiration)
        return !!token; // Return true if token exists, false otherwise
      }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        // localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return  '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        let emailData ={
            email
        }
        return this._httpClient.post(`${this.api_base_url}/admin/forgotPassword`, emailData);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: any): Observable<any>
    {
        return this._httpClient.post(`${this.api_base_url}/admin/resetPassword`, password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }
        return this._httpClient.post(`${this.api_base_url}/admin/loginAdmin`, credentials).pipe(
            switchMap((response: any) =>
            {
                // Store the access token in the local storage
                // this.accessToken = response.accessToken;
                if(response.code == 200){

                    localStorage.setItem('token',response?.data.token)
                    localStorage.setItem('admin',JSON.stringify(response?.data.admin))
                    localStorage.setItem('userPermission',JSON.stringify(response?.data.userPermission))
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    
                    // Store the user on the user service
                    this._userService.user = response.data?.admin;
                    location.reload()
                    // Return a new observable with the response
                    return of(response);
                }
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('token');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this.isAuthenticated() )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.isAuthenticated() )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
